//==============================================================================
// Test Locator Contact
//
// A link for each administrator that opens up a modal displaying contact
// information.
//==============================================================================
import * as React from 'react';
import classnames from 'classnames';

import { AppStatus } from '../../../utilities/test-constants';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';

import { ITestLocatorResources } from '../test-locator.props.autogenerated';
import { IAdministratorEntity } from '../../../actions/DataServiceEntities.g';

//==============================================================================
// INTERFACES
//==============================================================================
export interface ITestLocatorContactProps {
    cls: Function;
    resources: ITestLocatorResources;
    admin: IAdministratorEntity;
}

export interface ITestLocatorContactState extends React.ComponentState {
    isOpen: boolean;
}

//==============================================================================
// CLASS DEFINITION
//==============================================================================
/**
 * TestLocatorContact component
 * @extends {React.PureComponent<ITestLocatorContactProps, ITestLocatorContactState>}
 */
//==============================================================================
class TestLocatorContact extends React.PureComponent<ITestLocatorContactProps, ITestLocatorContactState> {
    //==========================================================================
    // VARIABLES
    //==========================================================================
    private contactLink: React.RefObject<any>;

    //==========================================================================
    // PUBLIC METHODS
    //==========================================================================
    //------------------------------------------------------
    // Constructor
    //------------------------------------------------------
    constructor(props: ITestLocatorContactProps) {
        super(props);
        this.state = { isOpen: false };
        this.contactLink = React.createRef();
    }

    //------------------------------------------------------
    // Render function
    //------------------------------------------------------
    public render(): JSX.Element {
        const { cls, resources, admin } = this.props;

        const testTypes = (admin.IowaStatus === AppStatus.approved && admin.StanfordStatus === AppStatus.approved) ?
            `${this.props.resources.testLocator_typeIowaShort} and ${this.props.resources.testLocator_typeStanfordShort}`
        : (
            admin.IowaStatus === AppStatus.approved ? this.props.resources.testLocator_typeIowaShort : this.props.resources.testLocator_typeStanfordShort
        );

        return (
            <>
                <Button
                    className={classnames(cls('admin-link'), 'contact')}
                    aria-label={resources.testLocator_contactLink}
                    title={resources.testLocator_contactLink}
                    onClick={this._toggle}
                    innerRef={this.contactLink}
                >
                    {resources.testLocator_contactLink}
                </Button>
                <Modal
                    autoFocus={true}
                    fade={false}
                    applicationNode={'rsg-root'}
                    returnFocusRef={this.contactLink}
                    isOpen={this.state.isOpen}
                    toggle={this._toggle}
                    className={classnames(cls('modal'), cls('contact'))}
                    horizontalPosition={'center'}
                    verticalPosition={'center'}
                >
                    <ModalHeader toggle={this._toggle} tag={'h4'} className={cls('contact-heading')}>
                        {resources.testLocator_profileHeading}
                    </ModalHeader>
                    <ModalBody>
                        <div className={cls('contact-description')}>
                            <span className={cls('contact-description-name')}>
                                {admin.Name}
                            </span>
                            <span className={cls('contact-description-text')}>
                                {resources.testLocator_profileDescriptionInterfix}
                            </span>
                            <span className={cls('contact-description-test')}>
                                {testTypes}
                            </span>
                            <span className={cls('contact-description-text')}>
                                {resources.testLocator_profileDescriptionSuffix}
                            </span>
                        </div>
                        <div className={cls('contact-info')}>
                            <h5 className={cls('contact-info-heading')}>
                                {resources.testLocator_profileContactHeading}
                            </h5>
                            <div className={cls('contact-info-description')}>
                                {admin.ContactInformation}
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter />
                </Modal>
            </>
        );
    }

    //==========================================================================
    // PRIVATE METHODS
    //==========================================================================
    //------------------------------------------------------
    // Toggle function
    //------------------------------------------------------
    private readonly _toggle = (): void => {
        this.setState({
            isOpen: !this.state.isOpen
        });
    };
}

export default TestLocatorContact;
